import { FC, ReactNode, useEffect, useMemo, useRef, useState } from "react";
import { useBotJsLoader } from "../../hooks";
import { ChatBotLoaderInstance, ChatBotSettings } from "../../types";
import { ChatBotContext, ChatBotContextValue } from "./ChatBotContext";

interface ChatBotProviderProps {
  token: string;
  chatBotUrl: string;
  children: ReactNode;
}

const JUGL_CHAT_BOT_SETTINGS: Partial<ChatBotSettings> = {
  buttonColor: "#2196F3",
  buttonIcon: "default-1",
  alignment: "right",
  sideSpacing: 20,
  bottomSpacing: 20,
  isButtonHidden: true,
  ctas: [],
  ctaVisual: null,
};

export const ChatBotProvider: FC<ChatBotProviderProps> = ({
  token,
  chatBotUrl,
  children,
}) => {
  const botJsUrl = `${chatBotUrl}/bot.min.js`;
  const { loaded } = useBotJsLoader(botJsUrl);
  const chatbotRef = useRef<ChatBotLoaderInstance | null>(null);
  const [isInitialized, setIsInitialized] = useState(false);

  useEffect(() => {
    // Cleanup previous instance if token changed
    if (chatbotRef.current) {
      chatbotRef.current.destroy();
      chatbotRef.current = null;
      setIsInitialized(false);
    }

    // Initialize new instance
    if (loaded && window.jugl?.ChatBot) {
      try {
        chatbotRef.current = window.jugl.ChatBot({
          token,
          chatbotUrl: chatBotUrl,
          settings: JUGL_CHAT_BOT_SETTINGS,
          settingsType: "loader-only",
        });
        setIsInitialized(true);
      } catch (err) {
        // oxlint-disable-next-line no-console
        console.error("Failed to initialize chatbot:", err);
        setIsInitialized(false);
      }
    } else {
      setIsInitialized(false);
    }

    // Cleanup on unmount
    return () => {
      if (chatbotRef.current) {
        chatbotRef.current.destroy();
        chatbotRef.current = null;
      }
    };
  }, [loaded, token, chatBotUrl]);

  const value: ChatBotContextValue = useMemo(
    () => ({
      isInitialized,
      toggle: () => chatbotRef.current?.toggleChat(),
      show: () => chatbotRef.current?.show(),
      hide: () => chatbotRef.current?.hide(),
    }),
    [isInitialized]
  );

  return (
    <ChatBotContext.Provider value={value}>{children}</ChatBotContext.Provider>
  );
};
